/**
 * HistoryPanel — shows change log for a record (create/update/delete events).
 */
import React, { useEffect, useState, useCallback } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Collapse from '@mui/material/Collapse'
import IconButton from '@mui/material/IconButton'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { ApiService } from '@/utils/api'

interface ChangeEntry {
  id: string
  action: string
  changed_fields: string[] | null
  old_values: Record<string, any> | null
  new_values: Record<string, any> | null
  changed_by: string
  changed_at: string
}

interface HistoryPanelProps {
  connectionId: string
  objectName: string
  recordId: string
}

const ACTION_COLORS: Record<string, 'success' | 'info' | 'error'> = {
  create: 'success',
  update: 'info',
  delete: 'error',
}

export function HistoryPanel({ connectionId, objectName, recordId }: HistoryPanelProps) {
  const [entries, setEntries] = useState<ChangeEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const resp = await ApiService.get<{ data: ChangeEntry[] }>(
        `/v1/app/${encodeURIComponent(connectionId)}/${encodeURIComponent(objectName)}/${encodeURIComponent(recordId)}/history?limit=50`,
      )
      setEntries(resp.data)
    } catch {
      setEntries([])
    }
    setLoading(false)
  }, [connectionId, objectName, recordId])

  useEffect(() => { load() }, [load])

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  if (entries.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
        <Typography variant="body2">No changes recorded yet</Typography>
      </Box>
    )
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 30 }} />
            <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Changed fields</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {entries.map((entry) => {
            const isExpanded = expandedId === entry.id
            const hasDetails = (entry.action === 'update' && entry.changed_fields && entry.changed_fields.length > 0)
              || (entry.action === 'create' && entry.new_values)
            return (
              <React.Fragment key={entry.id}>
                <TableRow hover sx={{ cursor: hasDetails ? 'pointer' : 'default' }} onClick={() => hasDetails && setExpandedId(isExpanded ? null : entry.id)}>
                  <TableCell sx={{ py: 0.5 }}>
                    {hasDetails && (
                      <IconButton size="small" sx={{ p: 0 }}>
                        {isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                      </IconButton>
                    )}
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    <Chip
                      label={entry.action}
                      size="small"
                      color={ACTION_COLORS[entry.action] || 'default'}
                      sx={{ height: 22, fontSize: 11, fontWeight: 600, textTransform: 'uppercase' }}
                    />
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    {entry.changed_fields ? (
                      <Typography variant="body2" sx={{ fontSize: 12, color: 'text.secondary' }}>
                        {entry.changed_fields.join(', ')}
                      </Typography>
                    ) : (
                      <Typography variant="body2" sx={{ fontSize: 12, color: 'text.disabled' }}>—</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    <Typography variant="body2" sx={{ fontSize: 12, fontFamily: 'monospace', color: 'text.secondary' }}>
                      {new Date(entry.changed_at).toLocaleString()}
                    </Typography>
                  </TableCell>
                </TableRow>
                {hasDetails && (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ py: 0, border: 0 }}>
                      <Collapse in={isExpanded}>
                        <Box sx={{ py: 1, pl: 5 }}>
                          {entry.action === 'update' && entry.changed_fields?.map((field) => (
                            <Box key={field} sx={{ display: 'flex', gap: 2, mb: 0.5, fontSize: 12 }}>
                              <Typography sx={{ fontWeight: 600, fontSize: 12, minWidth: 120 }}>{field}</Typography>
                              <Typography sx={{ fontSize: 12, color: 'error.main', textDecoration: 'line-through' }}>
                                {String(entry.old_values?.[field] ?? '')}
                              </Typography>
                              <Typography sx={{ fontSize: 12, color: 'success.main' }}>
                                {String(entry.new_values?.[field] ?? '')}
                              </Typography>
                            </Box>
                          ))}
                          {entry.action === 'create' && entry.new_values && (
                            <Box sx={{ fontSize: 12 }}>
                              {Object.entries(entry.new_values).map(([k, v]) => (
                                <Box key={k} sx={{ display: 'flex', gap: 2, mb: 0.25 }}>
                                  <Typography sx={{ fontWeight: 600, fontSize: 12, minWidth: 120, color: 'text.secondary' }}>{k}</Typography>
                                  <Typography sx={{ fontSize: 12 }}>{String(v ?? '')}</Typography>
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
